import { createDataLabel } from '@app/features/documents/utils/createDataLabel';
import { NewGetDraftByIdResponse } from '@app/store/transport-layer/document/drafts';
import { Field } from '@app/store/transport-layer/types/field';
import {
  ParticipantWithSigningProperties,
  SignerWithSigningProperties,
} from '@app/store/transport-layer/types/participant';

function convertDomainRequesterField(field: Field['ToReceive']): Field['ToReceive'] {
  switch (field.type) {
    case 'CHECKBOX':
      return {
        ...field,
        dataLabel: field.dataLabel ?? createDataLabel(),
      };
    case 'IMAGE':
      return {
        ...field,
        dataLabel: field.dataLabel ?? createDataLabel(),
      };
    case 'TEXT':
      return {
        ...field,
        dataLabel: field.dataLabel ?? createDataLabel(),
      };
    default:
      throw new Error(`Invalid requester field type: ${field.type}`);
  }
}

export default function transformDraftDataToDocumentModel(draft: NewGetDraftByIdResponse): {
  attachments: NewGetDraftByIdResponse['attachments'];
  participants: NewGetDraftByIdResponse['participants'];
  fields: NewGetDraftByIdResponse['fields'];
  fieldGroups: NewGetDraftByIdResponse['fieldGroups'];
  verifications: NewGetDraftByIdResponse['verifications'];
} {
  const requester = draft.participants?.find((participant) => participant.type === 'REQUESTER');

  const signers: SignerWithSigningProperties[] =
    draft.participants?.filter(
      (participant): participant is SignerWithSigningProperties => participant.type === 'SIGNER'
    ) ?? [];

  /**
   * 임베디드에서 전송 누락될 수도 있는 값을 추가해줍니다.
   * 1. order
   * 2. locale
   * 3. signingDuration
   *
   */
  const mappedSigners: SignerWithSigningProperties[] = assignParticipantSigningOrder(signers).map(
    (signer) => ({
      // id를 이용해서 attachment와 signer를 연결해야 해서 서버에서 준 값을 이용합니다
      id: signer.id ?? signer.dataLabel,
      name: signer.name ?? '',
      message: signer.message ?? '',
      locale: signer.locale ?? 'ko',
      role: signer.role,
      order: signer.order,
      // signingDuration 기본값은 14일 = 14 * 24 * 60 분 = 20160 분 입니다.
      signingDuration: signer.signingDuration ?? 20160,
      signingMethod: signer.signingMethod,
      contact: signer.contact ?? '',
      dataLabel: signer.dataLabel,
      signingPeriod: null,
      type: 'SIGNER',
      host: signer.host,
    })
  );

  const participantExceptSigners =
    draft.participants?.filter(
      (participant): participant is ParticipantWithSigningProperties =>
        participant.type !== 'SIGNER'
    ) ?? [];

  return {
    ...draft,
    participants: [...participantExceptSigners, ...mappedSigners],
    fields:
      draft.fields.map((field) => {
        if (field.participant.dataLabel === requester?.dataLabel) {
          return {
            ...convertDomainRequesterField(field),
          };
        }
        return field;
      }) ?? [],
    fieldGroups: draft.fieldGroups ?? [],
    attachments: draft.attachments.map((attachment) => ({
      ...attachment,
      dataLabel: attachment.dataLabel ?? createDataLabel(),
      config: {
        ...attachment.config,
        // 임베디드에서 전송 누락될 수 있는 케이스라 기본값을 true로 설정합니다.
        required: attachment.config?.required ?? true,
      },
    })),
  };
}

function isEverySignersHasSigningOrder(signers: SignerWithSigningProperties[]): boolean {
  return signers.every((signer) => signer.order !== undefined);
}

function assignParticipantSigningOrder(
  signers: SignerWithSigningProperties[]
): SignerWithSigningProperties[] {
  const tempSigners = signers.slice();
  if (!isEverySignersHasSigningOrder(tempSigners)) {
    return tempSigners.map((signer, index) => ({
      ...signer,
      order: index + 1,
    }));
  }
  // 순서없는 서명
  if (tempSigners.every((p) => p.order === 1)) {
    return tempSigners;
  }

  if (new Set(tempSigners.map((p) => p.order)).size !== tempSigners.length) {
    throw new Error(
      `Invalid participant signing order: ${tempSigners.map((p) => p.order).join(', ')}`
    );
  }
  // 빈 자리 제거
  tempSigners.sort((p1, p2) => p1.order - p2.order);
  return tempSigners.map((participant, index) => ({ ...participant, signingOrder: index + 1 }));
}
